import { useState } from "react";
import { toast } from "sonner";
import { Mail, MessageSquare, Send, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Name, email, and message are required.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent. Lisa will get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact"
        description="Questions about the system, a coaching inquiry, or just want to say hi? Send Lisa a message, usually a 1-2 day response."
        path="/contact"
      />
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand-sage">Say hi</p>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-brand-ink">
              Let's chat.
            </h1>
            <p className="mt-5 text-brand-inkSoft leading-relaxed">
              Questions about the system, a coaching inquiry, or just want to say a real
              parent built this and it helps? I'd love to hear from you.
            </p>

            <div className="mt-10 space-y-5">
              <div className="flex items-start gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white border border-brand-border text-brand-sage">
                  <Mail className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div>
                  <p className="text-sm font-medium text-brand-ink">Email</p>
                  <p className="text-sm text-brand-inkMuted">hello@onestepaheadparents.com</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white border border-brand-border text-brand-sage">
                  <MessageSquare className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div>
                  <p className="text-sm font-medium text-brand-ink">Response time</p>
                  <p className="text-sm text-brand-inkMuted">Usually within 1–2 business days.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white rounded-3xl border border-brand-border p-8 md:p-10 shadow-soft">
              {sent ? (
                <div data-testid="contact-success" className="text-center py-10">
                  <CheckCircle2 className="h-12 w-12 mx-auto text-brand-sage" />
                  <h3 className="mt-4 font-display text-2xl text-brand-ink">Message sent!</h3>
                  <p className="mt-2 text-brand-inkMuted">
                    Thanks for reaching out. Lisa will be in touch soon.
                  </p>
                  <button
                    onClick={() => setSent(false)}
                    data-testid="contact-send-another"
                    className="mt-6 inline-flex items-center gap-2 text-brand-sageDark font-medium"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-brand-inkMuted">
                        Name
                      </label>
                      <input
                        data-testid="contact-name"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        type="text"
                        placeholder="Your name"
                        className="mt-2 w-full bg-brand-bg border border-brand-border rounded-xl px-4 py-3 outline-none focus:border-brand-sage"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-brand-inkMuted">
                        Email
                      </label>
                      <input
                        data-testid="contact-email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        type="email"
                        placeholder="you@email.com"
                        className="mt-2 w-full bg-brand-bg border border-brand-border rounded-xl px-4 py-3 outline-none focus:border-brand-sage"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-brand-inkMuted">
                      Subject (optional)
                    </label>
                    <input
                      data-testid="contact-subject"
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      type="text"
                      placeholder="What's this about?"
                      className="mt-2 w-full bg-brand-bg border border-brand-border rounded-xl px-4 py-3 outline-none focus:border-brand-sage"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-brand-inkMuted">
                      Message
                    </label>
                    <textarea
                      data-testid="contact-message"
                      required
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      rows={6}
                      placeholder="Tell me what's on your mind..."
                      className="mt-2 w-full bg-brand-bg border border-brand-border rounded-xl px-4 py-3 outline-none focus:border-brand-sage resize-none"
                    />
                  </div>
                  <button
                    data-testid="contact-submit"
                    disabled={loading}
                    type="submit"
                    className="inline-flex items-center gap-2 bg-brand-sage text-white hover:bg-brand-sageDark transition-colors px-7 py-3.5 rounded-full font-medium disabled:opacity-70"
                  >
                    {loading ? "Sending..." : "Send Message"}
                    {!loading && <Send className="h-4 w-4" />}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
